<template>
  <div>
    <div v-if="!pod">
      File not found!
    </div>

    <div v-if="pod" class="vb-subtitles">

      <div class="d-flex align-center">
        <div class="flex-grow-1" data-cy="podFiles-subtitles-title">
          <h1>{{$t('routes.pod.menuSubtitles.subtitles')}}</h1>
        </div>

        <div>
          <v-btn color="primary"
                 elevation=0
                 @click="isTranscribeSelectedDialogVisible = true"
                 data-cy="podMenu-transcript-newTranscript-btn"
          >
            <v-icon>mdi-plus-circle</v-icon>
            {{ $t('common.new') }}
          </v-btn>
        </div>

        <div v-if="$canRunPaidService() && (isVideoPod || isAudioPod)">
          <v-menu v-model="assetMenuIsActive">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                rounded="32"
                variant="text"
                height="38"
                width="38"
                :aria-label="$t('routes.pod.menuSubtitles.newSubtitles')"
                icon
                tile
                data-cy="podFiles-subtitles-newSubtitleMenu-btn"
              >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item data-cy="podFiles-subtitles-uploadSubtitle-btn"
                           @click="isUploadSubtitlesDialogVisible = true">
                <template v-slot:prepend>
                  <v-icon size="small">mdi-file-upload</v-icon>
                </template>
                <v-list-item-title>
                  {{ $t('routes.pod.menuSubtitles.uploadSubtitle') }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <v-container fluid class="py-4 px-0">
        <PodSubtitleAssetsList
          v-model="subtitleAssetId"
          v-bind:assets="subtitleAssets"
          v-on:setAssetForEditing="setAssetForEditing"
          data-cy="podMenu-subtitles-list"
        />
      </v-container>

      <template v-if="loadSubtitlesError">
        <div class="bg-error my-2 pa-2 rounded">
          {{ loadSubtitlesError }}
        </div>
      </template>

      <template v-if="selectedAsset">
        <v-container fluid class="py-4 px-0">
          <v-row>
            <v-col cols="4">
              <v-btn
                color="primary"
                variant="flat"
                v-bind:disabled="this.isFormShown"
                @click="addNewLine"
              >
                <v-icon>mdi-file-edit</v-icon>
                {{ $t('routes.pod.menuSubtitles.newLine') }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="search"
                append-inner-icon="mdi-magnify"
                v-bind:label="$t('common.search')"
                variant="solo-filled"
                flat single-line hide-details
              />
            </v-col>
            <v-col cols="2">
              <v-overlay
                location-strategy="connected"
                location="bottom"
                :scrim="false"
              >
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    color="primary"
                    elevation=0
                    :aria-label="$t('routes.pod.menuSubtitles.filter')"
                  >
                    <v-icon>mdi-filter-cog-outline</v-icon>
                  </v-btn>
                </template>
                <div class="bg-list px-4 py-1 rounded-lg elevation-10">
                  <v-switch
                    v-model="subtitlesInVideo"
                    density="comfortable"
                    color="primary"
                    v-bind:label="subtitlesInVideo ? $t('routes.pod.menuSubtitles.disableSubInVideo') : $t('routes.pod.menuSubtitles.enableSubInVideo')"
                    hide-details
                    @update:model-value="toggleSubtitlesInVideo"
                  />
                </div>
              </v-overlay>
            </v-col>
          </v-row>
        </v-container>

        <v-container fluid class="py-4 px-0 subtitle-lines">
          <v-row class="font-weight-bold">
            <v-col cols="2">{{ $t('routes.pod.menuSubtitles.time') }}</v-col>
            <v-col cols="2">{{ $t('routes.pod.menuSubtitles.durationShort') }}</v-col>
            <v-col cols="8">{{ $t('routes.pod.menuSubtitles.lines') }}</v-col>
          </v-row>
          <v-row v-if="showCreationForm">
            <v-col cols="12">
              <PodSubtitlesForm
                v-bind:subtitleForEditing="subtitleForEditing"
                v-bind:pod="pod"
                v-bind:overlapWarnings="[]"
                v-on:visibility="showCreationForm = $event"
                data-cy="podMenu-subtitles-subtitlesForm"
              />
            </v-col>
          </v-row>
          <v-row v-for="(subtitle, index) in filteredSubtitles"
                 v-bind:key="subtitle.id"
                 v-on:dblclick="setSubtitleForEditing(subtitle.id)"
                 v-on:contextmenu.prevent="showContextMenu($event, subtitle)"
                 :ref="(el) => setSubtitleLineRef(el, index)"
                 class="subtitle-line mt-0"
          >

            <v-col cols="12">
              <v-row v-bind:class="getLineClasses(subtitle)" class="pa-3">
                <v-col cols="2">
                  {{ convertToTimeFormat(subtitle.startTime) }}
                </v-col>
                <v-col cols="2">{{ $t('routes.pod.menuSubtitles.nSecondsShort', duration(subtitle)) }} </v-col>
                <v-col cols="8" style="white-space: pre-wrap;">
                  {{ subtitle.text }}
                </v-col>
              </v-row>

              <v-row v-if="canEditSubscriptionLine(subtitle)">
                <v-col cols="12">
                  <PodSubtitlesForm
                    v-bind:subtitleForEditing="subtitleForEditing"
                    v-bind:pod="pod"
                    v-bind:overlapWarnings="getOverlapWarningsForSubtitle(subtitle.id)"
                    v-on:visibility="showForm = $event"
                    data-cy="podMenu-subtitles-subtitlesForm"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <!-- Context Menu -->
          <v-menu v-model="contextMenu.show" v-bind:target="[contextMenu.x, contextMenu.y]" absolute>
            <v-list>
              <v-list-item @click="editSubtitleLine()">
                <v-list-item-title>
                  <v-icon>mdi-pencil</v-icon>
                  {{ $t('common.edit') }}
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="deleteSubtitleLine()">
                <v-list-item-title>
                  <v-icon>mdi-delete-outline</v-icon>
                  {{ $t('common.delete') }}
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="contextMenu.subtitle && isOverlapping(contextMenu.subtitle.id)"
                @click="openFixOverlapsDialog()"
              >
                <v-list-item-title>
                  <v-icon>mdi-wrench</v-icon>
                  {{ $t('routes.pod.menuSubtitles.fixOverlaps') }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-container>
      </template>
    </div>
  </div>

  <TranscribeSelectedDialog
    v-if="showingCreateTranscriptBtn"
    v-model="isTranscribeSelectedDialogVisible"
    :pods="[pod]"
    transcriptionContext="podView"
  />

  <UploadFilesDialog
    v-if="getCurrentNode"
    v-model="isUploadSubtitlesDialogVisible"
    :node="getCurrentNode"
    :pod="pod"
    :uploadDialogType="'subtitles'"
  />

  <FixOverlapsDialog
    v-model="isFixOverlapsDialogVisible"
    :warnings="subtitleStore.overlapWarnings"
    :has-overlaps="subtitleStore.hasOverlaps"
    @fix-overlaps="handleFixOverlaps"
    @accept-overlaps="handleAcceptOverlaps"
  />
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useSubtitleStore } from '@/stores/SubtitleStore.js'
import { usePlayerStore } from '@/stores/PlayerStore.js'
import { useNodeStore } from '@/stores/NodeStore.js';
import PodSubtitleAssetsList from './PodSubtitleAssetsList.vue';
import PodSubtitlesForm from './PodSubtitlesForm.vue';
import UploadFilesDialog from '@/routes/nodes2/components/dialogs/UploadFilesDialog.vue';
import TranscribeSelectedDialog from '@/routes/nodes2/components/dialogs/TranscribeSelectedDialog.vue';
import FixOverlapsDialog from './dialogs/FixOverlapsDialog.vue';

export default {
  name: "SubtitlesToolbar",
  components: {
    TranscribeSelectedDialog,
    UploadFilesDialog,
    PodSubtitleAssetsList,
    PodSubtitlesForm,
    FixOverlapsDialog,
  },
  data() {
    return {
      search: null,
      subtitleAssetId: null,
      subtitleForEditing: null,
      showForm: false,
      showCreationForm: false,
      subtitlesInVideo: false,
      selectedSubtitleLine: null,
      pendingHighlightTime: null,
      isTranscribeSelectedDialogVisible: false,
      isUploadSubtitlesDialogVisible: false,
      isFixOverlapsDialogVisible: false,
      subtitleLineRefs: [],
      loadSubtitlesError: null,

      headers: [
        { text: 'Time', modelValue: 'startTime' },
        { text: 'Duration', modelValue: 'duration' },
        { text: 'Line', modelValue: 'text' },
      ],

      contextMenu: {
        show: false,
        x: 0,
        y: 0,
        subtitle: null,
      },
      assetMenuIsActive: false,
    };
  },
  mounted() {
    if (!this.pod) {
      return;
    }

    const subtitleStore = useSubtitleStore();
    subtitleStore.resetState();

    if (this.$route.query.menu === 'subtitles') {
      const assetId = this.$route.query.aid;
      const startTime = this.$route.query.start ? parseInt(this.$route.query.start) : null;

      if (assetId) {
        this.subtitleAssetId = assetId;

		// Track if we need to highlight a subtitle after loading
        if (startTime) {
          this.pendingHighlightTime = startTime;
        }

	    // Call setAssetForEditing which will trigger the API call
        this.setAssetForEditing(assetId);
      } else {
        this.reset();
      }
    }
  },
  watch: {
    subtitleAssetId: function(newValue) {
      if (newValue) {
        this.setAssetForEditing(newValue);
      }
    },
    showForm: function (value) {
      if (!value) {
        this.subtitleForEditing = null;
        this.selectedSubtitleLine = null;
      }
    },
    showCreationForm: function (value) {
      if (!value) {
        this.subtitleForEditing = null;
      }
    },
    playerCurrentTime: function() {
      if (this.subtitlesInVideo) {
        this.scrollToActiveLine();
      }
    },
    subtitles: function(newSubtitles) {
      if (newSubtitles.length > 0 && this.pendingHighlightTime) {
        this.highlightSubtitleByTime(this.pendingHighlightTime);
        this.pendingHighlightTime = null;
      }
    },
    '$route': {
      handler: 'handleRouteQueryChange',
      immediate: false,
      deep: true
    }
  },
  methods: {
    stub: function () {
    },
    setSubtitleLineRef(el, index) {
      if (el) {
        this.subtitleLineRefs[index] = el;
      }
    },
    closeMenu() {
      this.assetMenuIsActive = false;
    },
    setAssetForEditing: function(assetId) {
      this.subtitleAssetId = assetId;
      this.reset();

	  // Enable the subtitle in the video when subtitle file is selected
      this.subtitlesInVideo = true;

	  // Dispatch the action and store a reference to the promise
      const subtitleStore = useSubtitleStore();
      const loadPromise = subtitleStore.selectSubtitleAsset({ assetId });

      loadPromise.catch((err) => {
        this.loadSubtitlesError = this.$replaceT(
          err?.response?.data?.error?.message?.code,
          {},
          this.$t('common.generalError')
        );
      })
	  // If we have a pending highlight time from URL parameters
      if (this.pendingHighlightTime) {
        loadPromise.then(() => {
		  // Now that subtitles are loaded, we can highlight
          this.$nextTick(() => {
            this.highlightSubtitleByTime(this.pendingHighlightTime);
            this.pendingHighlightTime = null;
          });
        });
      }

      if (this.subtitlesInVideo) {
        this.setPlayerAsset();
      }
    },
    handleRouteQueryChange() {
      if (this.$route.query.menu === 'subtitles') {
        const transcriptId = this.$route.query.tid;
        const assetId = this.$route.query.aid;
        const startTime = this.$route.query.start ? parseInt(this.$route.query.start) : null;

        if (transcriptId && assetId) {
          this.subtitleAssetId = assetId;

          if (startTime) {
            this.pendingHighlightTime = startTime;
          }

          this.setAssetForEditing(assetId);
        }
      }
    },
    setSubtitleForEditing: function(subtitleId) {
      // First close any existing form
      this.showForm = false;
      this.showCreationForm = false;
      this.subtitleForEditing = null;
      this.selectedSubtitleLine = null;

      // Then open the new form in next tick
      this.$nextTick(() => {
        const foundFilteredSubtitle = this.filteredSubtitles.find((subtitle) => subtitle.id === subtitleId);
        this.subtitleForEditing = foundFilteredSubtitle;
        this.showForm = true;

        if (this.subtitlesInVideo) {
          const playerStore = usePlayerStore();
          playerStore.setSeekTimeEvent(foundFilteredSubtitle.startTime / 1000);
        }
      });
    },
    convertToTimeFormat(milliseconds) {
      let seconds = Math.floor((milliseconds / 1000) % 60),
        minutes = Math.floor((milliseconds / (1000 * 60)) % 60),
        hours = Math.floor((milliseconds / (1000 * 60 * 60)) % 24);

      hours = (hours < 10) ? "0" + hours : hours;
      minutes = (minutes < 10) ? "0" + minutes : minutes;
      seconds = (seconds < 10) ? "0" + seconds : seconds;

      return hours + ":" + minutes + ":" + seconds;
    },
    addNewLine: function () {
      // First close any existing form
      this.showForm = false;
      this.showCreationForm = false;
      this.subtitleForEditing = null;
      this.selectedSubtitleLine = null;

      // Then open the creation form in next tick
      this.$nextTick(() => {
        this.subtitleForEditing = {
          startTime: this.playerCurrentTime * 1000,
          endTime: (this.playerCurrentTime * 1000) + 2000,
          duration: 2,
          text: '',
        };
        this.showCreationForm = true;
      });
    },
    showContextMenu(event, subtitle) {
      event.preventDefault();
      event.stopPropagation();

      this.contextMenu.show = true;
      this.contextMenu.x = event.clientX;
      this.contextMenu.y = event.clientY;
      this.contextMenu.subtitle = subtitle;
    },
    editSubtitleLine() {
      const subtitleToEdit = this.contextMenu.subtitle;
      this.contextMenu.show = false;  // close the context menu

      // First close any existing form
      this.showForm = false;
      this.showCreationForm = false;
      this.subtitleForEditing = null;
      this.selectedSubtitleLine = null;

      // Then open the new form in next tick
      this.$nextTick(() => {
        this.subtitleForEditing = subtitleToEdit;
        this.showForm = true;
      });
    },
    deleteSubtitleLine() {
      this.contextMenu.show = false;  // close the context menu

      const subtitleToDelete = this.contextMenu.subtitle;

      if (this.subtitleForEditing && this.subtitleForEditing.id === subtitleToDelete.id) {
        this.showForm = false;
      }

      const payload = {
        subtitle: {
          id: subtitleToDelete.id,
        },
      };

      const subtitleStore = useSubtitleStore();
      subtitleStore.removeSubtitleLine(payload);

      this.saveAsset();
    },
    saveAsset: async function() {
      let self = this;

      const subtitleStore = useSubtitleStore();
      const cues = subtitleStore.getTransformedSubtitles;

      const data = {
        podID: this.pod.id,
        assetID: this.selectedAsset.id,
        payload: {
          cues,
          allowOverlaps: true,
        }
      };

      const podStore = usePodStore();

      try {
        const response = await podStore.updateSubtitleAsset(data);

        if (response?.warnings && response.warnings.length > 0) {
          const overlapWarnings = response.warnings.filter((w) => w.type === 'overlap');
          if (overlapWarnings.length > 0) {
            self.$showMessage(
              this.$t('routes.pod.menuSubtitles.savedOverlappingSubtitle'),
              { type: 'warning' }
            );
          }
        }
      } catch (error) {
        if (error && error.message) {
          self.$showMessage(error.message, { type: 'error' });
        } else {
          console.log(error);
          self.$showMessage(self.$t('backend.system-error'), { type: 'error' });
        }
      }
    },
    reset: function() {
      this.showForm = false;
      this.showCreationForm = false;
      this.subtitleForEditing = null;
      this.selectedSubtitleLine = null;
      this.subtitleLineRefs = [];
      this.loadSubtitlesError = null;
    },
    scrollToActiveLine: function() {
      if (!this.subtitleLineRefs || this.subtitleLineRefs.length === 0) {
        return;
      }

      const element = document.getElementsByClassName("active-subtitle")[0];
      if (element && element.classList && element.classList.contains('active-subtitle')) {
        element.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    },
    toggleSubtitlesInVideo: function(enabledVideoSubtitles) {
      if (enabledVideoSubtitles) {
        this.setPlayerAsset();
      }

      if (!enabledVideoSubtitles) {
        const playerStore = usePlayerStore();
        playerStore.setActiveSubtitle(null);
      }
    },
    setPlayerAsset() {
      if (this.selectedAsset) {
        const playerStore = usePlayerStore();
        playerStore.setActiveSubtitle(this.selectedAsset);
        playerStore.refreshActiveSubtitle();
      }
    },
    highlightSubtitleByTime(startTime) {
      const timeInMs = parseInt(startTime);

	  // Find the subtitle with the matching startTime
      const matchingSubtitle = this.subtitles.find(
        (subtitle) => subtitle.startTime === timeInMs
      );

      if (matchingSubtitle) {
		// Mark this subtitle as active
        this.selectedSubtitleLine = matchingSubtitle;

		// Scroll to the matching subtitle
        this.$nextTick(() => {
          this.scrollToSubtitleByTime(matchingSubtitle.id);
        });

		// If subtitles in video are enabled, also seek to this time
        if (this.subtitlesInVideo) {
          const playerStore = usePlayerStore();
          playerStore.setSeekTimeEvent(timeInMs / 1000);
        }
      }
    },
    scrollToSubtitleByTime(subtitleId) {
      if (!this.subtitleLineRefs || this.subtitleLineRefs.length === 0) {
        return;
      }

	  // Find the subtitle element in the refs array
      const index = this.filteredSubtitles.findIndex((subtitle) => subtitle.id === subtitleId);

      if (index >= 0 && this.subtitleLineRefs[index]) {
        const element = this.subtitleLineRefs[index];
        if (element && element.scrollIntoView) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    },
    isOverlapping(subtitleId) {
      const subtitleStore = useSubtitleStore();
      return subtitleStore.isSubtitleOverlapping(subtitleId);
    },
    getOverlapWarningsForSubtitle(subtitleId) {
      const subtitleStore = useSubtitleStore();
      return subtitleStore.overlapWarnings.filter((warning) =>
        warning.ids && warning.ids.includes(subtitleId),
      );
    },
    getLineClasses(subtitle) {
      const isEditingLine = this.subtitleForEditing && (this.subtitleForEditing.id === subtitle.id);
      const isHighlightedFromUrl = this.selectedSubtitleLine && (this.selectedSubtitleLine.id === subtitle.id);
      const isPlayingLine = this.subtitlesInVideo &&
        (
          this.playerCurrentTime >= (subtitle.startTime / 1000)
          &&
          this.playerCurrentTime < (subtitle.endTime / 1000)
        );

      return {
        'selected-line': isEditingLine || isHighlightedFromUrl,
        'active-subtitle': isPlayingLine || isHighlightedFromUrl,
        'overlapping-line': this.isOverlapping(subtitle.id),
      };
    },
    openFixOverlapsDialog() {
      this.contextMenu.show = false;
      this.isFixOverlapsDialogVisible = true;
    },
    handleFixOverlaps(strategy) {
      const subtitleStore = useSubtitleStore();

      if (strategy === 'split') {
        subtitleStore.splitOverlaps();
      } else if (strategy === 'merge') {
        subtitleStore.mergeOverlaps();
      }

      this.saveAsset();
    },
    handleAcceptOverlaps() {
      const subtitleStore = useSubtitleStore();
      subtitleStore.setAllowOverlaps(true);
      this.saveAsset();
    },
  },
  computed: {
    getCurrentNode: function() {
      const nodeStore = useNodeStore();
      const currentNode = nodeStore.getCurrentNode;
	  // Return null if undefined to avoid prop validation errors
      return currentNode || null;
    },
    pod: function() {
      const podStore = usePodStore();
      return podStore.getViewPod;
    },
    subtitleAssets: function () {
      const subtitleStore = useSubtitleStore();
      return subtitleStore.getSubtitleAssets;
    },
    selectedAsset: function () {
      const subtitleStore = useSubtitleStore();
      return subtitleStore.getSelectedAsset;
    },
    isVideoPod: function() {
      const podStore = usePodStore();
      return podStore.isVideoPod;
    },
    isAudioPod: function() {
      const podStore = usePodStore();
      return podStore.isAudioPod;
    },
    subtitles: function() {
      const subtitleStore = useSubtitleStore();
      return subtitleStore.getSubtitles;
    },
    filteredSubtitles: function()  {
      if (!this.search) {
        return this.subtitles;
      }
      const search = this.search.toLowerCase();
      return this.subtitles.filter((subtitle) => subtitle.text.toLowerCase().includes(search));
    },
    canEditSubscriptionLine: function() {
      return (subtitle) => {
        return this.showForm && this.subtitleForEditing && (this.subtitleForEditing.id === subtitle.id);
      }
    },
    duration: function() {
      return (subtitle) => {
        const result = (subtitle.endTime - subtitle.startTime) / 1000;
        return Number(result.toFixed());
      }
    },
    isFormShown: function() {
      return this.showForm || this.showCreationForm;
    },
    playerCurrentTime: function() {
      const playerStore = usePlayerStore();
      return playerStore.currentTime;
    },
    showingCreateTranscriptBtn() {
      return this.isVideoPod || this.isAudioPod;
    },
    subtitleStore() {
      return useSubtitleStore();
    },
  }
}
</script>

<style lang="scss" scoped>
.no-selection {
  user-select: none !important;
}

.selected-line {
  background-color: #3dabff !important;
  color: black;
  border-radius: 0;
}

.active-subtitle {
  background-color: #3dabff;
  color: black;
  border-radius: 8px;
}

.vb-subtitles .v-input--selection-controls {
  margin: 0 !important;
}

.overlapping-line {
  background-color: rgba(255, 152, 0, 0.08);
  border-left: 3px solid #ff9800;
  padding-left: 12px;
  transition: all 0.3s ease;

  &:hover {
    background-color: rgba(255, 152, 0, 0.15);
  }
}

:deep(.v-theme--dark) {
  .overlapping-line {
    background-color: rgba(255, 152, 0, 0.12);

    &:hover {
      background-color: rgba(255, 152, 0, 0.2);
    }
  }
}
</style>
